import { useEffect, useState } from 'react';
import type { QuoteView } from '@contracts';
import { useDomainApi } from '@ui/shared/domain/DomainApiContext';
import { Repeat, Bookmark, Check } from '@ui/shared/icons';
import { QUOTES } from '@domain/content/quotes';

/**
 * Phone Sentence of the day (R8.11, today-phone-parity.md §4.2 — moved and
 * simplified per owner request, today-view.md §1.25a v2.7). Originally one
 * of the six collapsed-by-default cards in the "More" stack below DUE
 * TODAY/NEXT UP (§4.1's AC-P4); the owner asked for it always-visible,
 * directly under the "Daily Entry" header instead, with no card
 * title/chrome — just the sentence itself plus its existing Shuffle/Save
 * actions (those stay: they're real functionality, not the redundant
 * "Sentence of the day" label the ask was to drop). Same interaction as
 * desktop's `SentenceOfDayCard` (pure client-side shuffle,
 * `saveQuote`/`getSentenceOfDayState` for the shown quote).
 */
export function SentenceOfDayCardPhone() {
  const api = useDomainApi();
  const [quote, setQuote] = useState<QuoteView | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setQuote(pickInitialQuote());
  }, []);

  useEffect(() => {
    if (!quote) return;
    let cancelled = false;
    void api.getSentenceOfDayState(quote.quoteId).then((state) => {
      if (cancelled) return;
      setSaved(state.saved);
    });
    return () => {
      cancelled = true;
    };
  }, [api, quote]);

  function shuffle() {
    setQuote((current) => pickDifferentQuote(current));
  }

  function save() {
    if (!quote) return;
    void api.saveQuote(quote.quoteId).then(() => setSaved(true));
  }

  if (!quote) return null;

  return (
    <div
      style={{
        padding: '0 var(--layout-page-margin-phone)',
        marginTop: 'var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <p style={{ margin: 0, fontSize: 'var(--type-body-size)', lineHeight: 1.5, color: 'var(--text-primary)' }}>
        {quote.text}
      </p>
      <div style={{ marginTop: 'var(--space-1)', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)' }}>
        — {quote.author}
      </div>
      <div style={{ marginTop: 'var(--space-2)', display: 'flex', gap: 'var(--space-4)' }}>
        <button
          type="button"
          onClick={shuffle}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            minHeight: 'var(--touch-target-min)',
            padding: '0 var(--space-2) 0 0',
            color: 'var(--text-secondary)',
            fontSize: 'var(--type-caption-size)',
            fontWeight: 600,
          }}
        >
          <Repeat size={16} aria-hidden="true" />
          Shuffle
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saved}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            minHeight: 'var(--touch-target-min)',
            padding: '0 var(--space-2)',
            color: saved ? 'var(--hue-primary)' : 'var(--text-secondary)',
            fontSize: 'var(--type-caption-size)',
            fontWeight: 600,
          }}
        >
          {saved ? <Check size={16} aria-hidden="true" /> : <Bookmark size={16} aria-hidden="true" />}
          {saved ? 'Saved' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function pickInitialQuote(): QuoteView {
  const index = Math.floor(Math.random() * QUOTES.length);
  return QUOTES[index] ?? QUOTES[0]!;
}

/** Cycles to a different quote — never repeats the immediately-prior quote when the set has >1 entry. */
function pickDifferentQuote(current: QuoteView | null): QuoteView {
  if (QUOTES.length <= 1) return QUOTES[0]!;
  let next = current;
  while (!next || next.quoteId === current?.quoteId) {
    const index = Math.floor(Math.random() * QUOTES.length);
    next = QUOTES[index] ?? QUOTES[0]!;
  }
  return next;
}
